import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { fetchPanchangContent } from "@/lib/api";
import { PANCHANG_GLOSSARY } from "@/lib/panchangGlossary";
import { Divider } from "@/components/MotifBackground";
import { Sparkles, BookOpen, Search, ArrowRight } from "lucide-react";

export default function PanchangExtras() {
  const [content, setContent] = useState({ features_intro: "", features: [] });
  const [glossary] = useState(PANCHANG_GLOSSARY);
  const [q, setQ] = useState("");

  useEffect(() => {
    fetchPanchangContent().then(setContent).catch(() => {});
  }, []);

  const filtered = useMemo(() => {
    const t = q.trim().toLowerCase();
    const list = (glossary || []).slice().sort((a, b) =>
      (a.transliteration || a.term || "").localeCompare(b.transliteration || b.term || ""));
    if (!t) return list;
    return list.filter((g) =>
      (g.transliteration || g.term || "").toLowerCase().includes(t) ||
      (g.devanagari || "").includes(q.trim()) ||
      (g.meaning || "").toLowerCase().includes(t));
  }, [glossary, q]);

  return (
    <div className="mx-auto max-w-5xl px-5 md:px-8">
      {/* FEATURES */}
      {content.features?.length > 0 && (
        <section data-testid="panchang-features">
          <Divider className="my-12" />
          <div className="flex flex-wrap items-end justify-between gap-3">
            <div>
              <h2 className="flex items-center gap-2 font-serif-latin text-2xl text-ink"><Sparkles className="h-5 w-5 text-saffron" /> Features</h2>
              {content.features_intro && <p className="mt-1.5 max-w-3xl text-sm text-ink-3 md:text-base">{content.features_intro}</p>}
            </div>
            <Link to="/panchang/how-it-works" data-testid="how-it-works-link" className="inline-flex items-center gap-1.5 border border-line px-4 py-2 text-sm text-ink-2 transition-colors hover:border-saffron hover:text-saffron">
              <BookOpen className="h-4 w-4" /> How it works <ArrowRight className="h-3.5 w-3.5" />
            </Link>
          </div>
          <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {content.features.map((f, i) => (
              <div key={i} data-testid={`feature-card-${i}`} className="border border-line bg-surface p-5 transition-colors hover:border-[var(--gold-muted)]/70">
                <p className="font-serif-latin text-lg leading-snug text-ink">{f.title}</p>
                <p className="mt-2 text-sm leading-relaxed text-ink-2 deva-lh">{f.desc}</p>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* GLOSSARY */}
      {glossary?.length > 0 && (
        <section data-testid="panchang-glossary">
          <Divider className="my-12" />
          <div className="flex flex-wrap items-end justify-between gap-3">
            <div>
              <h2 className="flex items-center gap-2 font-serif-latin text-2xl text-ink"><BookOpen className="h-5 w-5 text-saffron" /> Panchangam Glossary</h2>
              <p className="mt-1.5 text-sm text-ink-3">Every Panchangam term, explained — with its Devanagari.</p>
            </div>
            <div className="relative w-full max-w-xs">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-3" />
              <input value={q} onChange={(e) => setQ(e.target.value)} data-testid="glossary-search" placeholder="Search terms…"
                className="w-full border border-line bg-parchment py-2 pl-9 pr-3 text-sm text-ink focus:border-saffron focus:outline-none" />
            </div>
          </div>
          <dl className="mt-6 divide-y divide-line border-y border-line" data-testid="glossary-list">
            {filtered.map((g, i) => (
              <div key={g.id || i} className="grid gap-1 py-4 sm:grid-cols-[minmax(0,14rem)_1fr] sm:gap-6" data-testid={`glossary-row-${(g.transliteration || g.term || '').toLowerCase().replace(/\s+/g, '-')}`}>
                <dt className="flex items-baseline gap-2">
                  <span className="font-serif-latin text-base text-ink">{g.transliteration || g.term}</span>
                  {g.devanagari && <span className="font-deva text-sm text-ink-2 deva-lh">{g.devanagari}</span>}
                </dt>
                <dd className="text-sm leading-relaxed text-ink-2">{g.meaning}</dd>
              </div>
            ))}
            {filtered.length === 0 && <p className="py-6 text-sm text-ink-3">No terms match “{q}”.</p>}
          </dl>
        </section>
      )}
    </div>
  );
}
